import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';

import { useCurrentUser, useSession } from '@/features/auth';
import type { CurrentUser } from '@/shared/types/domain';

interface AuthContextValue {
  session: Session | null;
  isSessionLoading: boolean;
  /**
   * `undefined` = ainda buscando; `null` = sessão válida mas sem vínculo
   * vigente (US-04) — trate os dois de forma diferente, nunca como sinônimos.
   */
  currentUser: CurrentUser | null | undefined;
  currentUserError: unknown;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Sessão + pessoa + papéis, resolvidos uma vez no topo da árvore (ADR-001).
 * `RequireAuth` decide o que fazer com esse estado; este provider só expõe.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { session, isLoading: isSessionLoading } = useSession();
  const { data: currentUser, error: currentUserError } = useCurrentUser();

  const value = useMemo<AuthContextValue>(
    () => ({ session, isSessionLoading, currentUser, currentUserError }),
    [session, isSessionLoading, currentUser, currentUserError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() precisa estar dentro de <AuthProvider>.');
  return ctx;
}
